import { useEffect, useMemo, useState } from 'react';

import { useSplitTestBranch } from '@api/lendingAPI/lapiHooks';
import {
    TFormalOffer,
    TRBLSchedule
} from '@helpers/FormalOffers/formalOffers.types';
import { getFormalOffersMaxAvailableCash } from '@helpers/FormalOffers/getFormalOffersMaxAvailableCash';
import { getHasOnlyRCFOffer } from '@helpers/FormalOffers/getHasOnlyRCFOffer';
import { getIsRBLOffer } from '@helpers/FormalOffers/getIsRBLOffer';
import { getIsRCFOffer } from '@helpers/FormalOffers/getIsRCFOffer';
import { getTimeOutDate } from '@helpers/FormalOffers/getTimeOutDate';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { logError } from '@iwLogger/IwDataDogLogger';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAngleLeft } from '@iwoca/icons';
import {
    fetchGetFormalOffers,
    fetchGetFormalOffersCreditFacilityConfiguration,
    fetchGetFormalOffersFlexi24Configuration,
    fetchGetFormalOffersMerchantCashAdvanceConfiguration,
    fetchPutFormalOffersCreditFacilityConfiguration,
    fetchPutFormalOffersFlexi24Configuration,
    fetchPutFormalOffersMerchantCashAdvanceConfiguration
} from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { getMonthsfromDays } from '../../../multiLoanDashboard/accountsSelector';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { MainSectionWrapper } from '../../../pageStructure/mainSectionWrapper';
import { TitleSectionWrapper } from '../../../pageStructure/titleSectionWrapper/TitleSectionWrapper';
import { getMinAvailableCash } from '../../MyOffers.helpers';
import ApprovalHeader from '../approvalHeader/ApprovalHeader';
import { getMaxDate } from '../earlyRepaymentCalculator/EarlyRepaymentHelper';
import { OfferConfigurationViewUI } from '../offerConfigurationView/OfferConfigurationView';
import { RCFApprovedView } from '../RCFApprovedView/RCFApprovedView';
import { XSellTitle } from '../xsell/xsellTitle/xsellTitle';

export const FlexiApprovedView = ({
    setShowAllOptions
}: {
    setShowAllOptions?: (showAllOptions: boolean) => void;
}) => {
    const stateKey = useStateKey();

    async function createCustomerOfferConfiguration({
        formalOffer,
        sweep,
        drawdownAmount,
        stateKey
    }: {
        formalOffer: TFormalOffer;
        sweep?: number;
        drawdownAmount: number;
        stateKey: string;
    }) {
        if (getIsRBLOffer(formalOffer!) && sweep) {
            await fetchPutFormalOffersMerchantCashAdvanceConfiguration({
                stateKey,
                offerId: formalOffer.offer_id!,
                body: {
                    data: {
                        cash_amount: drawdownAmount,
                        revenue_sweep_fraction: sweep,
                        step: 'offer'
                    }
                }
            });
        } else if (formalOffer.product_type === 'credit_facility') {
            await fetchPutFormalOffersCreditFacilityConfiguration({
                stateKey,
                offerId: formalOffer.offer_id!,
                body: { data: { cash_amount: drawdownAmount, step: 'offer' } }
            });
        } else if (formalOffer.product_type === 'flexi24') {
            await fetchPutFormalOffersFlexi24Configuration({
                stateKey,
                offerId: formalOffer.offer_id!,
                body: { data: { cash_amount: drawdownAmount, step: 'offer' } }
            });
        }
    }

    const debouncedCreateCustomerOfferConfig = useMemo(
        () => _.debounce(createCustomerOfferConfiguration, 250),
        []
    );

    useEffect(() => {
        return () => {
            debouncedCreateCustomerOfferConfig.cancel();
        };
    }, [debouncedCreateCustomerOfferConfig]);

    const priorityCreateCustomerOfferConfig: typeof createCustomerOfferConfiguration =
        (opts) => {
            debouncedCreateCustomerOfferConfig.cancel();
            return createCustomerOfferConfiguration(opts);
        };

    const [drawdownAmount, setDrawdownAmount] = useState<number>(0);
    const [selectedOffer, setSelectedOffer] = useState<
        TFormalOffer | null | undefined
    >(undefined);
    const [hasSelectedRCF, setHasSelectedRCF] = useState(false);
    const [rblSchedule, setRblSchedule] = useState<TRBLSchedule>({
        schedule: null,
        scheduleLoading: true
    });

    const {
        isLoading,
        isError,
        data: getFormalOffersResponse
    } = useQuery({
        queryKey: ['formal_offers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey })
    });

    useEffect(() => {
        if (getFormalOffersResponse?.data) {
            setDrawdownAmount((current) =>
                current === 0
                    ? getMinAvailableCash(
                          getFormalOffersResponse.data.formal_offers
                      )
                    : current
            );
        }
    }, [getFormalOffersResponse, setDrawdownAmount]);

    useEffect(() => {
        let mounted = true;
        const hasDrawdownAmountBeenModified =
            drawdownAmount !== 0 &&
            (!getFormalOffersResponse?.data ||
                drawdownAmount !==
                    getMinAvailableCash(
                        getFormalOffersResponse.data.formal_offers
                    ));

        if (
            getFormalOffersResponse?.data &&
            selectedOffer === undefined &&
            !hasDrawdownAmountBeenModified
        ) {
            Promise.all(
                getFormalOffersResponse.data.formal_offers.map(
                    async (formalOffer) => {
                        switch (formalOffer.product_type) {
                            case 'credit_facility':
                                return {
                                    formalOffer,
                                    config: await fetchGetFormalOffersCreditFacilityConfiguration(
                                        {
                                            stateKey,
                                            offerId: formalOffer.offer_id!
                                        }
                                    )
                                };
                            case 'flexi24':
                                return {
                                    formalOffer,
                                    config: await fetchGetFormalOffersFlexi24Configuration(
                                        {
                                            stateKey,
                                            offerId: formalOffer.offer_id!
                                        }
                                    )
                                };
                            case 'merchant_cash_advance':
                                return {
                                    formalOffer,
                                    config: await fetchGetFormalOffersMerchantCashAdvanceConfiguration(
                                        {
                                            stateKey,
                                            offerId: formalOffer.offer_id!
                                        }
                                    )
                                };
                            default:
                                return null;
                        }
                    }
                )
            )
                .then((results) => {
                    const nonEmptyResults = results
                        .filter((result) => result !== null)
                        .filter((result) => !!result.config.data);
                    const latest = _.maxBy(nonEmptyResults, (result) =>
                        new Date(result.config.data!.created!).getTime()
                    );

                    if (mounted) {
                        if (latest) {
                            setSelectedOffer(latest.formalOffer);
                            setDrawdownAmount(latest.config.data!.cash_amount!);
                            if (getIsRCFOffer(latest.formalOffer)) {
                                setHasSelectedRCF(true);
                            }
                            return;
                        }

                        if (
                            getFormalOffersResponse.data.formal_offers
                                .length === 1
                        ) {
                            setSelectedOffer(
                                getFormalOffersResponse.data.formal_offers[0]
                            );
                        }
                    }
                })
                .catch((error) => {
                    logError(
                        'Failed to preselect formal offer configuration',
                        {},
                        error
                    );
                });
        }
        return () => {
            mounted = false;
        };
    }, [
        getFormalOffersResponse,
        stateKey,
        selectedOffer,
        drawdownAmount,
        setSelectedOffer,
        setDrawdownAmount
    ]);

    const updateSelectedOffer = (offer: TFormalOffer | null) => {
        if (!offer) {
            setSelectedOffer(null);
        } else {
            tracking.ampli.formalOfferSelected({
                formal_offer_id: offer.offer_id!,
                duration: getDurationInMonths(offer?.duration!)
            });
            setSelectedOffer(offer);
            debouncedCreateCustomerOfferConfig({
                formalOffer: offer,
                sweep:
                    rblSchedule.schedule?.data.summary.sweep
                        .selected_sweep_fraction || undefined,
                drawdownAmount,
                stateKey
            });
        }
    };

    const updateDrawdownAmount = (drawdownAmount: number) => {
        setDrawdownAmount(drawdownAmount);
        if (selectedOffer) {
            debouncedCreateCustomerOfferConfig({
                formalOffer: selectedOffer,
                sweep:
                    rblSchedule.schedule?.data.summary.sweep
                        .selected_sweep_fraction || undefined,
                drawdownAmount,
                stateKey
            });
        }
    };

    const updateRblSchedule = (next: TRBLSchedule) => {
        if (!_.isEqual(rblSchedule, next)) {
            setRblSchedule(next);
            if (selectedOffer) {
                debouncedCreateCustomerOfferConfig({
                    formalOffer: selectedOffer,
                    sweep:
                        next.schedule?.data.summary.sweep
                            .selected_sweep_fraction || undefined,
                    drawdownAmount,
                    stateKey
                });
            }
        }
    };

    const { branch: interestOnlySplitTest } = useSplitTestBranch({
        test: 'new_customer_interest_only_add_on_test_2025_q2',
        suspense: false
    });

    const hasOnlyRCFOffer = getFormalOffersResponse?.data
        ? getHasOnlyRCFOffer(getFormalOffersResponse.data.formal_offers)
        : false;

    const hasUninitalisedDrawdownAmount =
        drawdownAmount === 0 && !hasSelectedRCF && !hasOnlyRCFOffer;

    if (
        !isError &&
        (isLoading || hasUninitalisedDrawdownAmount || !interestOnlySplitTest)
    ) {
        return <MainContentPending data-testid="flexi-approval-view-loading" />;
    }

    if (isError) {
        return <FallbackErrorModal />;
    }

    const maxCashAmount = getFormalOffersMaxAvailableCash(
        getFormalOffersResponse!.data!.formal_offers
    );
    const timeoutDate = getTimeOutDate(
        getFormalOffersResponse!.data!.formal_offers
    );
    const hasRecommendedOffer = Boolean(
        getFormalOffersResponse.data.formal_offers[0].suitability_info
    );

    let maxDate = new Date().toISOString();
    let loanDuration = 0;

    if (selectedOffer) {
        loanDuration =
            selectedOffer.duration!.unit === 'days'
                ? getMonthsfromDays(selectedOffer.duration!.amount)
                : selectedOffer.duration!.amount;

        maxDate = getMaxDate(selectedOffer.duration!.amount);
    }

    const formalOffers = getFormalOffersResponse.data.formal_offers;

    if (hasSelectedRCF || hasOnlyRCFOffer)
        return (
            <div className="py-3xl">
                <RCFApprovedView
                    formalOffers={formalOffers}
                    showOtherOffers={() => {
                        setHasSelectedRCF(false);
                        setDrawdownAmount(getMinAvailableCash(formalOffers));
                    }}
                    initialAmount={drawdownAmount}
                />
            </div>
        );

    return (
        <MainSectionWrapper>
            {hasRecommendedOffer && setShowAllOptions ? (
                <div className="mb-xl flex flex-col items-center justify-center max-sm:mb-xl">
                    <h1 className="text-center max-sm:leading-10">
                        <FormattedMessage
                            id="_.recommended_offer_view.heading"
                            defaultMessage="You're approved for up to {amount}"
                            values={{
                                amount: (
                                    <span className="text-success-drk">
                                        <FormattedCurrency
                                            value={drawdownAmount}
                                        />
                                    </span>
                                )
                            }}
                        />
                    </h1>
                    <p>
                        <FormattedMessage
                            id="_.recommended_offer_view.offer_details.timeout"
                            defaultMessage="This offer is valid until {timeout}"
                            values={{
                                timeout: (
                                    <FormattedDate
                                        value={new Date(
                                            getFormalOffersResponse.data
                                                .formal_offers[0].timeout!
                                        ).toISOString()}
                                        day="2-digit"
                                        month="long"
                                        year="numeric"
                                    />
                                )
                            }}
                        />
                    </p>

                    <div className="self-start pb-s">
                        <Button
                            variant="tertiary"
                            onClick={() => setShowAllOptions(false)}
                        >
                            <IconAngleLeft className="fill-primary-40" />
                            <FormattedMessage
                                id="_.flexi_approved_view.go_back"
                                defaultMessage="Go back"
                            />
                        </Button>
                    </div>
                </div>
            ) : getCountry() === 'DE' ? (
                <XSellTitle
                    expiryDate={timeoutDate}
                    maxCashAmount={maxCashAmount}
                />
            ) : (
                <TitleSectionWrapper>
                    <ApprovalHeader
                        approvalStatusTimeOut={timeoutDate}
                        maxCashAmount={maxCashAmount}
                    />
                </TitleSectionWrapper>
            )}
            <OfferConfigurationViewUI
                maxDate={maxDate}
                loanDuration={loanDuration}
                drawdownAmount={drawdownAmount}
                updateDrawdownAmount={updateDrawdownAmount}
                selectedOffer={selectedOffer}
                updateSelectedOffer={updateSelectedOffer}
                formalOffers={getFormalOffersResponse.data!}
                interestOnlySplitTest={interestOnlySplitTest!}
                createCustomerOfferConfiguration={
                    priorityCreateCustomerOfferConfig
                }
                rblSchedule={rblSchedule}
                updateRblSchedule={updateRblSchedule}
                selectRCFOffer={() => setHasSelectedRCF(true)}
            />
        </MainSectionWrapper>
    );
};
